import type { KeyEstimate } from '../analysis/types';
import { mod } from '../util/math';
import type { Chord } from './chord';
import { QUALITIES } from './chord';

const SHARP_GLYPHS = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const FLAT_GLYPHS = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

/** Major keys written with flats: F, B♭, E♭, A♭, D♭, G♭ (design: music-logic.md). */
const FLAT_MAJOR_TONICS = new Set([5, 10, 3, 8, 1, 6]);

/** Whether a key is written with flats. Minor keys follow their relative major. */
export function keyUsesFlats(tonic: number, mode: KeyEstimate['mode']): boolean {
  const relativeMajor = mode === 'major' ? tonic : mod(tonic + 3, 12);
  return FLAT_MAJOR_TONICS.has(mod(relativeMajor, 12));
}

export function noteName(pc: number, flats: boolean): string {
  return (flats ? FLAT_GLYPHS : SHARP_GLYPHS)[mod(pc, 12)]!;
}

/** "D major", "E♭ major", "C minor". */
export function formatKey(key: { tonic: number; mode: KeyEstimate['mode'] }): string {
  return `${noteName(key.tonic, keyUsesFlats(key.tonic, key.mode))} ${key.mode}`;
}

/** Chord name spelled for a key: "B♭", "F♯m", "N.C." for no chord. */
export function formatChord(
  chord: Chord | null,
  key: { tonic: number; mode: KeyEstimate['mode'] },
): string {
  if (!chord) return 'N.C.';
  return noteName(chord.root, keyUsesFlats(key.tonic, key.mode)) + QUALITIES[chord.quality].suffix;
}
